// PostHog analytics — full-coverage wiring for a state-driven app.
//
// Hopscotch navigates by Zustand state, not URLs, so PostHog's default
// pageview tracking would see one page forever. Coverage comes from three
// layers:
//   1. posthog-js built-ins: autocapture (clicks/inputs), history_change
//      pageviews (the real-URL auth routes), session recording (inputs
//      masked — kids product), web vitals, exception capture.
//   2. A store subscription (wireAnalytics) that turns state navigation
//      into synthetic $pageview events with stable /module/view paths,
//      identifies/resets the person as `user` changes, registers the
//      active learner as a super property, and derives domain events from
//      state transitions (generation started/finished, game played,
//      companion opened, print, upload).
//   3. A handful of explicit capture() calls at funnel moments that never
//      touch the store (placement started/completed, learner created,
//      invite checks, companion message sent).
//
// Init is key-gated: no VITE_PUBLIC_POSTHOG_KEY → every export is a no-op.
// Dev builds stay silent unless VITE_POSTHOG_DEV=true, so local sessions
// don't pollute product analytics.

import posthog from 'posthog-js'
import { useGlobalState } from '@/stores/globalState'
import type { GlobalAppState } from '@/stores/globalState'

let enabled = false

export function initAnalytics() {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined
  const devOptIn = import.meta.env.VITE_POSTHOG_DEV === 'true'
  if (!key || (import.meta.env.DEV && !devOptIn)) return

  posthog.init(key, {
    api_host: (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com',
    // Real-URL navigations (the /auth/* pages) — state navigation inside
    // the app is covered by the store subscription below.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_exceptions: true,
    autocapture: true,
    // HOP-140: the learner's (child's) name renders as plain DOM text in
    // 10+ places (learner switcher, home greeting, book detail, guide
    // previews, results views, dev tools...) — new ones get added
    // regularly. A per-component allowlist/blocklist would silently miss
    // the next one, so we mask autocapture text globally instead of
    // trying to tag every render site. Same call the team already made
    // for session_recording.maskAllInputs below.
    mask_all_text: true,
    person_profiles: 'identified_only',
    session_recording: {
      // Never record what parents/kids type — names, emails, codes.
      maskAllInputs: true,
      // HOP-140: rrweb only masks input values by default — general DOM
      // text (e.g. the learner's name shown throughout the app) still
      // rendered in plain text in replays. Mask all text nodes for the
      // same COPPA reason as maskAllInputs above.
      maskTextSelector: '*',
      // Selective unmask for content surfaces (games, evaluation, book
      // analysis, upload, guide previews). Mask-by-default stays the
      // fail-safe: an untagged new surface renders masked, never leaked.
      //
      // THE CONTRACT (see src/lib/privacyMask.tsx):
      //  - a content surface opts its subtree into visible replay text by
      //    spreading PH_UNMASK on its root element;
      //  - any learner-name (or other PII) render INSIDE an unmasked zone
      //    must be wrapped in <MaskedName> / PH_MASK — closest() picks the
      //    nearest marker, so the inner mask wins;
      //  - keep this function cheap: it runs per text node while recording.
      maskTextFn: (text, element) => {
        const zone = element?.closest?.('[data-ph-mask], [data-ph-unmask]')
        return zone?.hasAttribute('data-ph-unmask') ? text : '*'.repeat(text.length)
      },
    },
  })

  enabled = true
  wireAnalytics()
}

/** Safe capture — no-op until initAnalytics has run with a key. */
export function capture(event: string, properties?: Record<string, unknown>) {
  if (!enabled) return
  posthog.capture(event, properties)
}

// ─── Synthetic paths for state-driven navigation ─────────────────────────────

function syntheticPath(s: GlobalAppState): string {
  switch (s.activeModule) {
    case 'MY_GUIDES': {
      if (s.workspaceView === 'GENERATING') return '/home/generating'
      if (s.workspaceView === 'CANVAS') return '/home/set-canvas'
      if (s.workspaceView === 'FOCUSED')
        return s.activeWorkspaceItem === 'new' ? '/home/upload' : '/home/set-preview'
      return '/home'
    }
    case 'BOOKS': {
      if (s.workspaceView === 'BOOK_ANALYSIS') return '/books/analysis'
      if (s.workspaceView === 'BOOK_DETAIL') return '/books/detail'
      if (s.workspaceView === 'CHAPTER_SELECT') return '/books/chapters'
      return '/books'
    }
    case 'EVALUATION': {
      if (s.workspaceView === 'FOCUSED' && s.activeWorkspaceItem === 'evaluation-flow')
        return '/profile/placement'
      if (s.workspaceView === 'FOCUSED' && s.activeWorkspaceItem === 'evaluation-results')
        return '/profile/results'
      return '/profile'
    }
    case 'LEARNING_JOURNEY':
      return '/journey'
    default:
      return `/${String(s.activeModule).toLowerCase()}`
  }
}

// ─── Store subscription — navigation, identity, derived domain events ────────

function wireAnalytics() {
  const origin = window.location.origin

  // Snapshot-vs-previous diffing: zustand's plain subscribe hands us both.
  useGlobalState.subscribe((state, prev) => {
    // Identity follows the store user everywhere it's set (profile sync,
    // invite wall) and cleared (logout) — no per-call-site wiring.
    if (state.user?.id !== prev.user?.id) {
      if (state.user) {
        posthog.identify(state.user.id, {
          email: state.user.email,
          name: state.user.name,
        })
      } else {
        posthog.reset()
      }
    }

    // Active learner as a super property — every subsequent event segments
    // by learner and reading stage without threading it through call sites.
    if (state.activeLearnerId !== prev.activeLearnerId) {
      if (state.activeLearner) {
        posthog.register({
          learner_id: state.activeLearner.id,
          learner_stage: state.activeLearner.readingStage,
          learner_evaluated: state.activeLearner.evaluationComplete,
        })
        capture('learner_selected')
      } else {
        posthog.unregister('learner_id')
        posthog.unregister('learner_stage')
        posthog.unregister('learner_evaluated')
      }
    }

    // State navigation → synthetic pageviews.
    const path = syntheticPath(state)
    if (path !== syntheticPath(prev)) {
      posthog.capture('$pageview', {
        $current_url: `${origin}${path}`,
        workspace_item: state.activeWorkspaceItem,
        book_id: state.selectedBookId,
      })
    }

    // Domain events derived from state transitions (single wiring point —
    // Book Analysis, Chapter Selection, upload, and the companion all
    // funnel through the same store actions).
    if (state.isGenerating && !prev.isGenerating) {
      capture('game_generation_started', { guide_id: state.activeWorkspaceItem })
    }
    if (!state.isGenerating && prev.isGenerating) {
      const games = Object.values(state.generatedGames)
      capture('game_generation_finished', {
        guide_id: state.activeWorkspaceItem,
        games_total: games.length,
        games_complete: games.filter((g) => g.status === 'complete').length,
        games_failed: games.filter((g) => g.status === 'failed').length,
      })
    }
    if (state.playingGameId && state.playingGameId !== prev.playingGameId) {
      capture('game_played', { game_id: state.playingGameId })
    }
    if (state.companionOpen && !prev.companionOpen) {
      capture('companion_opened')
    }
    if (state.printAllRequest && state.printAllRequest !== prev.printAllRequest) {
      capture('print_all_requested', { guide_id: state.printAllRequest })
    }
    if (state.uploadEntry && state.uploadEntry !== prev.uploadEntry) {
      capture('upload_opened', { entry: state.uploadEntry })
    }
    if (state.addLearnerOpen && !prev.addLearnerOpen) {
      capture('add_learner_opened')
    }
    if (state.inviteRequired && !prev.inviteRequired) {
      capture('invite_wall_shown')
    }
  })
}
